import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as fromUser from '@titan/devkit/user';
import * as userActions from '@titan/devkit/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { generateAppRouteFromRedirectParams } from './auth.effects.helpers';
import { TitanConfigService } from '@titan/devkit/config';

@Injectable({
    providedIn: 'root',
})
export class AuthEffects {
    private actions$ = inject(Actions);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private config = inject(TitanConfigService);
    private readonly document = inject(DOCUMENT);

    logoutSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fromUser.logoutSuccess),
                tap(() => {
                    const { origin } = this.document.location;
                    const redirectUrl = new URL(this.config.gaiaUrlPath, origin).href;
                    this.document.location.replace(redirectUrl);
                })
            ),
        { dispatch: false }
    );

    saveLandingUrlToLocalStorageAndRedirect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromUser.loginAloyInitiate),
            filter(() => this.route.snapshot.queryParams['appUrl'] || this.route.snapshot.queryParams['url']),
            tap(() => {
                const prevAppRoute = generateAppRouteFromRedirectParams(this.route.snapshot.queryParams);
                window.sessionStorage.setItem('landingPageUrl', prevAppRoute);
            }),
            map(action => fromUser.loginAloy({ ssoUrl: action.ssoUrl }))
        )
    );

    redirectIfNotSavingLandingUrl$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromUser.loginAloyInitiate),
            filter(() => !(this.route.snapshot.queryParams['appUrl'] || this.route.snapshot.queryParams['url'])),
            map(action => fromUser.loginAloy({ ssoUrl: action.ssoUrl }))
        )
    );

    redirectToAloyLogin$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fromUser.loginAloy),
                tap(action => {
                    this.document.location.href = `${action.ssoUrl}?RelayState=${encodeURIComponent(this.config.gaiaUrlPath)}`;
                })
            ),
        { dispatch: false }
    );

    authCheckFailure$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(userActions.authenticateFailed),
                map(({ payload }) => payload),
                tap(url => {
                    const gaiaRedirectPath = `${this.config.gaiaUrlPath}/#/login`;
                    let redirectUri;
                    try {
                        redirectUri = new URL(gaiaRedirectPath, this.document.location.origin).href;
                    } catch (err) {
                        redirectUri = new URL('/#/login', this.document.location.origin).href;
                    }
                    const redirectUrl = `${redirectUri}?url=${url}`;
                    this.document.location.replace(redirectUrl);
                })
            ),
        { dispatch: false }
    );

    checkAppRedirect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(fromUser.loginSuccess),
                filter(() => this.route.snapshot.queryParams['appUrl']),
                map(() => {
                    const { origin } = this.document.location;
                    const prevAppRoute = generateAppRouteFromRedirectParams(this.route.snapshot.queryParams);
                    const newLocation = `${origin}${prevAppRoute}`;
                    return newLocation;
                }),
                filter(r => !!r),
                switchMap((newLocation: string) =>
                    this.actions$.pipe(
                        ofType(userActions.setTitanCookieSuccess),
                        tap(() => {
                            this.document.location.href = newLocation;
                        })
                    )
                )
            ),
        { dispatch: false }
    );

    loginSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(userActions.loginSuccess),
                tap(() => {
                    if (this.route.snapshot.queryParams['url']) {
                        this.router.navigateByUrl(this.route.snapshot.queryParams['url']);
                    } else {
                        this.router.navigate(['/browse']);
                    }
                })
            ),
        { dispatch: false }
    );
}
